import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { UntypedFormGroup } from '@angular/forms';

import {
  AccountMetadata,
  StorageAccountService,
} from 'app/providers/storage-api/blender-account.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { ChartDisplayOptions } from '../../../../models';
import { BigNumberChartOptionsData } from '../big-number-chart-options/big-number-chart-options.component';

interface BigNumberParams {
  analyticItem: string;
  scoreRange: string;
  scoringStrategy: string;
  boxSize: '1' | '2' | '3';
  boxDirection: 'TOP' | 'BOTTOM';
  increaseColor: string;
  decreaseColor: string;
  showVsTotal: boolean;
  showDisplayName: boolean;
  displayDataAs: 'absolute' | 'percentage';
  intPrecision: 1 | 2 | 3;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-big-number-chart-options-dialog',
  templateUrl: './big-number-chart-options-dialog.component.html',
  styleUrls: ['./big-number-chart-options-dialog.component.scss'],
  standalone: false,
})
export class BigNumberChartOptionsDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() public data: ChartDisplayOptions;
  @Output() modalClosed: EventEmitter<any> = new EventEmitter<ChartDisplayOptions>();

  public twoBigNumbers = false;
  private availableFields: AccountMetadata;
  public bigNumberDataOne: BigNumberChartOptionsData;
  public bigNumberDataTwo: BigNumberChartOptionsData;

  public bigNumberChartFormOne: UntypedFormGroup;
  public bigNumberChartFormTwo: UntypedFormGroup;
  public isLoading: boolean;
  public isFormInvalid = true;

  constructor(private accountService: StorageAccountService) {}

  async ngOnInit() {
    this.isLoading = true;
    this.twoBigNumbers = this.data.payload.analyticItemTwo;
    const isComparisonOrImpact = this.data.widgetGroup?.isImpact || this.data.payload?.comparison;
    const payload = this.data.payload;
    this.availableFields = await this.accountService.getFieldsForCurrentGroup();

    this.bigNumberDataOne = {
      field: 'one',
      isComparisonOrImpact,
      analyticItemType: this.getAnalyticItemType(payload.analyticItem),
      displayName: payload.metadataDisplayNameOne,
      analyticItem: payload.analyticItem,
      scoreRange: payload.scoreRange,
      scoringStrategy: payload.scoringStrategy,
      boxSize: payload.boxSize,
      boxDirection: payload.boxDirection,
      increaseColor: payload.increaseColor,
      decreaseColor: payload.decreaseColor,
      showVsTotal: payload.showVsTotal,
      showDisplayName: payload.showDisplayName,
      displayDataAs: payload.displayDataAs,
      intPrecision: payload.intPrecisionOne,
    };

    if (this.twoBigNumbers) {
      this.bigNumberDataTwo = {
        field: 'two',
        isComparisonOrImpact,
        analyticItemType: this.getAnalyticItemType(payload.analyticItemTwo),
        displayName: payload.metadataDisplayNameTwo,
        analyticItem: payload.analyticItemTwo,
        scoreRange: payload.scoreRangeTwo,
        scoringStrategy: payload.scoringStrategyTwo,
        boxSize: payload.boxSizeTwo,
        boxDirection: payload.boxDirectionTwo,
        increaseColor: payload.increaseColorTwo,
        decreaseColor: payload.decreaseColorTwo,
        showVsTotal: payload.showVsTotalTwo,
        showDisplayName: payload.showDisplayNameTwo,
        displayDataAs: payload.displayDataAsTwo,
        intPrecision: payload.intPrecisionTwo,
      };
    }
    this.isLoading = false;
  }

  private getAnalyticItemType(analyticItem: string) {
    if (analyticItem === 'documents') {
      return 'documents';
    } else {
      const matchingField = this.availableFields.metadata.find((f) => f.fieldName === analyticItem);
      if (matchingField.metadata.rating === true) {
        return 'rating';
      }
      return matchingField.fieldType as 'documents' | 'long' | 'double';
    }
  }

  onFormChanged(form: UntypedFormGroup, formNumber: 'formOne' | 'formTwo') {
    formNumber === 'formOne'
      ? (this.bigNumberChartFormOne = form)
      : (this.bigNumberChartFormTwo = form);
    this.validateForms();
  }

  private validateForms() {
    let formOneInvalid = false;
    let formTwoInvalid = false;
    if (this.bigNumberChartFormOne) {
      formOneInvalid = this.bigNumberChartFormOne.invalid;
    }
    if (this.bigNumberChartFormTwo) {
      formTwoInvalid = this.bigNumberChartFormTwo.invalid;
    }

    this.isFormInvalid = formOneInvalid || formTwoInvalid;
  }

  onCancel() {
    this.modalClosed.emit(null);
  }

  private resetChartOptionsPayloadValues() {
    const p = this.data.payload;
    p.scoreRange = undefined;
    p.scoringStrategy = undefined;
    p.boxSize = undefined;
    p.boxDirection = undefined;
    p.increaseColor = undefined;
    p.decreaseColor = undefined;
    p.showVsTotal = undefined;
    p.showDisplayName = undefined;
    p.displayDataAs = undefined;
    //
    p.scoreRangeTwo = undefined;
    p.scoringStrategyTwo = undefined;
    p.boxSizeTwo = undefined;
    p.boxDirectionTwo = undefined;
    p.increaseColorTwo = undefined;
    p.decreaseColorTwo = undefined;
    p.showVsTotalTwo = undefined;
    p.showDisplayNameTwo = undefined;
    p.displayDataAsTwo = undefined;
  }

  private mapFormValuesToParams(form: UntypedFormGroup, data: BigNumberChartOptionsData) {
    const minTarget = form.value.targetRatingToggle;
    const params: any = {};
    params.scoringStrategy =
      data.analyticItemType === 'documents'
        ? 'SUM'
        : form.value.scoringStrategyRadios.toUpperCase();
    params.scoreRange = form.value.maxTarget ? `${minTarget}:${form.value.maxTarget}` : undefined;

    if (!form.get('boxDirectionSelect').disabled) {
      params.boxSize = form.value.boxSizeSelect;
      params.boxDirection = form.value.boxDirectionSelect;
    }
    if (!form.get('increaseRadios').disabled) {
      params.increaseColor = form.value.increaseRadios;
      params.decreaseColor = form.value.decreaseRadios;
    }
    params.showVsTotal = form.value.showVsTotal;
    params.showDisplayName = form.value.showDisplayName;
    params.displayDataAs = form.value.displayDataAs;
    params.intPrecision = form.value.intPrecisionSelect;
    return params;
  }

  private mapParamsToWidgetPayload(numberOne: boolean, params: BigNumberParams) {
    const p = this.data.payload;
    if (numberOne) {
      p.scoreRange = params.scoreRange;
      p.scoringStrategy = params.scoringStrategy;
      p.boxSize = params.boxSize;
      p.boxDirection = params.boxDirection;
      p.increaseColor = params.increaseColor;
      p.decreaseColor = params.decreaseColor;
      p.showVsTotal = params.showVsTotal;
      p.showDisplayName = params.showDisplayName;
      p.displayDataAs = params.displayDataAs;
      p.intPrecisionOne = params.intPrecision;
    } else {
      p.scoreRangeTwo = params.scoreRange;
      p.scoringStrategyTwo = params.scoringStrategy;
      p.boxSizeTwo = params.boxSize;
      p.boxDirectionTwo = params.boxDirection;
      p.increaseColorTwo = params.increaseColor;
      p.decreaseColorTwo = params.decreaseColor;
      p.showVsTotalTwo = params.showVsTotal;
      p.showDisplayNameTwo = params.showDisplayName;
      p.displayDataAsTwo = params.displayDataAs;
      p.intPrecisionTwo = params.intPrecision;
    }
  }

  onSave() {
    this.resetChartOptionsPayloadValues();
    const paramsOne: BigNumberParams = this.mapFormValuesToParams(
      this.bigNumberChartFormOne,
      this.bigNumberDataOne
    );
    this.mapParamsToWidgetPayload(true, paramsOne);

    if (this.twoBigNumbers) {
      const paramsTwo: BigNumberParams = this.mapFormValuesToParams(
        this.bigNumberChartFormTwo,
        this.bigNumberDataTwo
      );
      this.mapParamsToWidgetPayload(false, paramsTwo);
    }
    this.data.needRefresh = true;

    this.modalClosed.emit(this.data);
  }
}
